import React, { useEffect, useState } from "react";
import { db, auth } from "../firebaseConfig";
import { collection, getDocs, deleteDoc, doc, getDoc } from "firebase/firestore";
import { Button, Card, Col, Row, ProgressBar, Form, Alert, Table } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import CryptoJS from "crypto-js";

const CLOUDINARY_API_SECRET = process.env.REACT_APP_CLOUDINARY_API_SECRET;
const CLOUDINARY_API_KEY = process.env.REACT_APP_CLOUDINARY_API_KEY;
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;


console.log(CLOUDINARY_API_KEY);
console.log(CLOUDINARY_API_SECRET)
console.log(CLOUDINARY_CLOUD_NAME)

export default function StorageMaintenance() {
  const [files, setFiles] = useState([]);
  const [signedDocuments, setSignedDocuments] = useState([]);
  const [totalStorageUsed, setTotalStorageUsed] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const MAX_STORAGE = 25 * 1024 * 1024 * 1024; // 25GB in bytes

  // Check if the user is an admin
  const checkAdminRole = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(db, "Users", user.uid));
        if (userDoc.exists() && userDoc.data().role === "admin") {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      }
    } catch (err) {
      console.error("Error checking user role:", err);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAdminRole();
  }, []);

  // Fetch files from Firestore
  const fetchFiles = async () => {
    try {
      const fileSnapshot = await getDocs(collection(db, "Files"));
      const fileList = fileSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setFiles(fileList);

      // Calculate total storage used
      const totalSize = fileList.reduce((acc, file) => acc + file.size, 0);
      setTotalStorageUsed(totalSize);
    } catch (err) {
      console.error("Error fetching files:", err);
    }
  };

  // Fetch signed documents from Firestore
  const fetchSignedDocuments = async () => {
    try {
      const signedDocsSnapshot = await getDocs(collection(db, "SignedDocuments"));
      const signedDocsList = signedDocsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSignedDocuments(signedDocsList);
    } catch (err) {
      console.error("Error fetching signed documents:", err);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchFiles();
      fetchSignedDocuments();
    }
  }, [isAdmin]);

  // Delete a file from Cloudinary
  const deleteFromCloudinary = async (fileUrl) => {
    try {
      const resourceType = fileUrl.includes(".pdf") ? "image" : "raw";
      const publicId = fileUrl.split("/").pop().split(".")[0];

      // Generate timestamp and signature
      const timestamp = Math.floor(Date.now() / 1000);
      const stringToSign = `public_id=${publicId}&timestamp=${timestamp}${CLOUDINARY_API_SECRET}`;
      const signature = CryptoJS.SHA1(stringToSign).toString(CryptoJS.enc.Hex);

      // Make the delete request
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${resourceType}/destroy`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            public_id: publicId,
            timestamp,
            signature,
            api_key: CLOUDINARY_API_KEY,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete file from Cloudinary");
      }

      console.log(response)

      console.log(`File ${publicId} deleted from Cloudinary.`);
    } catch (err) {
      console.error("Error deleting from Cloudinary:", err);
      throw err;
    }
  };

  // Delete files by date range
  const handleDeleteByDateRange = async () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }

    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();

    const filesToDelete = files.filter((file) => {
      const uploadedAt = new Date(file.uploadedAt.seconds * 1000).getTime();
      return uploadedAt >= start && uploadedAt <= end;
    });

    if (filesToDelete.length === 0) {
      alert("No files found in the specified date range.");
      return;
    }

    try {
      for (const file of filesToDelete) {
        await deleteFromCloudinary(file.url);
        await deleteDoc(doc(db, "Files", file.id));
      }

      alert("Files deleted successfully!");
      fetchFiles();
    } catch (err) {
      console.error("Error deleting files:", err);
      alert("Failed to delete files.");
    }
  };

  // Delete files older than 20 days
  const handleDeleteOldFiles = async () => {
    const now = new Date().getTime();
    const days20 = 20 * 24 * 60 * 60 * 1000;

    const filesToDelete = files.filter((file) => {
      const uploadedAt = new Date(file.uploadedAt.seconds * 1000).getTime();
      return now - uploadedAt > days20;
    });

    if (filesToDelete.length === 0) {
      alert("No files older than 20 days were found.");
      return;
    }

    try {
      for (const file of filesToDelete) {
        await deleteFromCloudinary(file.url);
        await deleteDoc(doc(db, "Files", file.id));
      }

      alert("Files older than 20 days have been deleted successfully!");
      fetchFiles();
    } catch (err) {
      console.error("Error deleting old files:", err);
      alert("Failed to delete old files.");
    }
  };

  // Delete a signed document
  const handleDeleteSignedDocument = async (docId) => {
    try {
      const docRef = signedDocuments.find((doc) => doc.id === docId);

      if (docRef) {
        if (docRef.signedUrl) await deleteFromCloudinary(docRef.signedUrl);
        if (docRef.unsignedUrl) await deleteFromCloudinary(docRef.unsignedUrl);
      }

      await deleteDoc(doc(db, "SignedDocuments", docId));
      alert("Signed document deleted successfully!");
      fetchSignedDocuments();
    } catch (err) {
      console.error("Error deleting signed document:", err);
      alert("Failed to delete signed document.");
    }
  };

  if (loading) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-secondary mt-3">Loading helpdesk data...</p>
          </div>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  if (!isAdmin) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <div className="bg-gradient-danger text-white rounded-4 p-4 shadow-lg">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-4 me-3"></i>
              <div>
                <h6 className="mb-0 fw-bold">Access Denied</h6>
                <p className="mb-0">You do not have permission to access this page.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-customer-service-2-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">Helpdesk Service</h1>
          </div>
          <p className="lead text-muted">Manage storage, files, and system maintenance</p>
        </div>
        {/* Enhanced Storage Management Section */}
        <Row className="g-4 mb-5">
          <Col md="6">
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-primary text-white p-4" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="mb-0 fw-bold">Storage Usage</h6>
                    <h2 className="mb-0 fw-bold">{(totalStorageUsed / (1024 * 1024 * 1024)).toFixed(2)} GB</h2>
                  </div>
                  <i className="ri-hard-drive-2-line fs-1 opacity-75"></i>
                </div>
              </div>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <span className="text-muted">Used: {(totalStorageUsed / (1024 * 1024 * 1024)).toFixed(2)} GB</span>
                  <span className="text-muted">Total: 25 GB</span>
                </div>
                <ProgressBar
                  now={(totalStorageUsed / MAX_STORAGE) * 100}
                  label={`${((totalStorageUsed / MAX_STORAGE) * 100).toFixed(2)}%`}
                  className="shadow-sm"
                  style={{ height: '12px', borderRadius: '10px' }}
                />
                <div className="mt-3">
                  <div className="d-flex align-items-center">
                    <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                      <i className="ri-database-line text-primary"></i>
                    </div>
                    <div>
                      <p className="text-muted mb-0 small">Storage capacity</p>
                      <p className="fw-semibold mb-0">25 GB Total</p>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6">
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-danger text-white p-4" 
                   style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-calendar-line fs-4 me-3"></i>
                  <h6 className="mb-0 fw-bold">Delete Files by Date Range</h6>
                </div>
              </div>
              <Card.Body className="p-4">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    <i className="ri-calendar-event-line me-2"></i>Start Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="form-control-lg shadow-sm border-2"
                    style={{ borderRadius: '12px' }}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    <i className="ri-calendar-check-line me-2"></i>End Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="form-control-lg shadow-sm border-2"
                    style={{ borderRadius: '12px' }}
                  />
                </Form.Group>
                <Button 
                  variant="danger" 
                  onClick={handleDeleteByDateRange}
                  className="w-100 py-3 rounded-pill fw-semibold shadow-lg"
                  style={{ 
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <i className="ri-delete-bin-line me-2"></i>Delete Files
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Enhanced Delete Old Files Section */}
        <Card className="border-0 shadow-lg rounded-4 mb-5">
          <div className="bg-gradient-warning text-white p-4 rounded-top-4" 
               style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <div className="d-flex align-items-center">
              <i className="ri-time-line fs-4 me-3"></i>
              <h5 className="mb-0 fw-bold">Delete Old Files</h5>
            </div>
          </div>
          <Card.Body className="p-4">
            <div className="bg-light rounded-3 p-4 mb-4">
              <div className="d-flex align-items-center">
                <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                  <i className="ri-information-line text-warning fs-4"></i>
                </div>
                <div>
                  <h6 className="fw-bold text-warning mb-1">Automatic Cleanup</h6>
                  <p className="text-muted mb-0">Delete all files that were uploaded more than 20 days ago to free up storage space.</p>
                </div>
              </div>
            </div>
            <Button 
              variant="warning" 
              size="lg"
              onClick={handleDeleteOldFiles}
              className="px-5 py-3 rounded-pill fw-semibold shadow-lg"
              style={{ 
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <i className="ri-delete-bin-line me-2"></i>Delete Files Older than 20 Days
            </Button>
          </Card.Body>
        </Card>

        {/* Enhanced Signed Documents Section */}
        <Card className="border-0 shadow-lg rounded-4">
          <div className="bg-gradient-info text-white p-4 rounded-top-4" 
               style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="ri-file-signature-line fs-4 me-3"></i>
                <h5 className="mb-0 fw-bold">Signed Documents</h5>
              </div>
              <span className="badge bg-white text-primary px-3 py-2 rounded-pill">
                {signedDocuments.length} documents
              </span>
            </div>
          </div>
          <Card.Body className="p-4">
            <div className="bg-primary bg-opacity-10 rounded-3 p-3 mb-4">
              <div className="d-flex align-items-center">
                <i className="ri-information-line text-primary me-2"></i>
                <span className="text-primary fw-semibold">Below is a list of all signed documents in the system.</span>
              </div>
            </div>
            <div className="table-responsive">
              <Table className="table-hover table-borderless">
                <thead className="bg-primary text-white">
                  <tr>
                    <th className="border-0">
                      <i className="ri-mail-line me-2"></i>Email
                    </th>
                    <th className="border-0">
                      <i className="ri-check-line me-2"></i>Signed Status
                    </th>
                    <th className="border-0">
                      <i className="ri-file-text-line me-2"></i>Signed URL
                    </th>
                    <th className="border-0">
                      <i className="ri-file-line me-2"></i>Unsigned URL
                    </th>
                    <th className="border-0">
                      <i className="ri-calendar-line me-2"></i>Created At
                    </th>
                    <th className="border-0">
                      <i className="ri-settings-3-line me-2"></i>Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {signedDocuments.map((doc) => (
                    <tr key={doc.id}
                        style={{ 
                          cursor: "pointer",
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                          e.currentTarget.style.transform = 'scale(1.01)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}>
                      <td className="fw-semibold">{doc.createdFor}</td>
                      <td>
                        <span className={`badge rounded-pill px-3 py-2 fw-semibold ${
                          doc.signedStatus === "signed" 
                            ? "bg-success" 
                            : "bg-warning text-dark"
                        }`}>
                          {doc.signedStatus}
                        </span>
                      </td>
                      <td>
                        {doc.signedUrl ? (
                          <a 
                            href={doc.signedUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-primary btn-sm rounded-pill"
                          >
                            <i className="ri-eye-line me-1"></i>View Signed
                          </a>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td>
                        {doc.unsignedUrl ? (
                          <a 
                            href={doc.unsignedUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-sm rounded-pill"
                          >
                            <i className="ri-eye-line me-1"></i>View Unsigned
                          </a>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td className="text-muted">
                        {new Date(doc.createdAt.seconds * 1000).toLocaleDateString()}
                      </td>
                      <td>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteSignedDocument(doc.id)}
                          className="rounded-pill"
                        >
                          <i className="ri-delete-bin-line me-1"></i>Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {signedDocuments.length === 0 && (
              <div className="text-center py-5">
                <div className="mb-4">
                  <i className="ri-file-signature-line text-muted" style={{ fontSize: '4rem' }}></i>
                </div>
                <h5 className="text-muted mb-3">No Signed Documents Found</h5>
                <p className="text-muted">No signed documents have been created yet.</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
      <Footer />
    </React.Fragment>
  );
}
